import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Group } from 'core/models';
import { ProfileService, StoreService } from 'core/services';
import { GroupStudentsScoreService } from '../../services/group-students-score/group-students-score.service';
import { TableDatasource, DataTypes, TableColumn } from '@gamelearn/skeletor/models';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { StudentsScore } from '@models/students-score';

@Component({
  selector: 'lms-group-students-score',
  templateUrl: './group-students-score.component.html',
  styleUrls: ['./group-students-score.component.scss']
})
export class GroupStudentsScoreComponent implements OnInit, OnDestroy, OnChanges {
  @Input() group: Group;
  @Input() studentScore: StudentsScore;
  public filters: any;
  public toolbarConfig: any;
  public dataSource: any;
  public groupStudentsScoreListId = 'GROUP_STUDENT_POINT_LIST';
  private userRole: any;
  private studentsScoreSubscription: Subscription = new Subscription();

  constructor(
    private groupStudentsService: GroupStudentsScoreService,
    private profileService: ProfileService,
    private translateService: TranslateService
  ) {}

  public ngOnInit() {
    this.toolbarConfig = {
      list: {
        listId: this.groupStudentsScoreListId,
        download: true,
        filterRow: true,
        columnChooser: true,
        hideSearch: false,
        skeletor: true
      }
    };

    this.studentsScoreSubscription = this.getLoggedUserRole$().subscribe(() => {
      this.dataSource = this.getDataSource(this.group, this.groupStudentsScoreListId, this.userRole, this.studentScore);
      StoreService.create(this.groupStudentsScoreListId, this.dataSource);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.dataSource && changes['studentScore']['currentValue']?.autoRefresh) {
      this.dataSource.refreshData();
    }
  }

  public ngOnDestroy(): void {
    this.studentsScoreSubscription.unsubscribe();
  }

  /**
   * Fill the data with the table
   * @param group
   * @param listId
   * @param userRole
   * @param studentScore
   */
  public getDataSource(group: Group, listId: string, userRole: any, studentScore: StudentsScore) {
    const dataSource = new TableDatasource({
      docs: studentScore.docs,
      columns: this.getColumns(studentScore.columns, !!studentScore.docs.length),
      limit: studentScore.limit,
      page: studentScore.page,
      pages: studentScore.pages,
      total: studentScore.total,
      sort: `-${studentScore.columns.find((column) => column['isRanking'])?.['id'] || studentScore.columns[0]?.['id']}`,
      load: (props) => this.loadTableDataSource(props, dataSource, group).subscribe(() => dataSource.refresh()),
      storeState: {
        enabled: true,
        key: `${listId}_${userRole.name}`
      }
    });

    if (!dataSource.docs.length) {
      this.setToolbarTable(true);
      dataSource.pristine = false;
    }

    StoreService.create(listId, dataSource);
    return dataSource;
  }

  /**
   * Set the data of the table
   * @param dataSource
   */
  public setDatasourceUpdate(dataSource: any): void {
    this.dataSource = dataSource;
    StoreService.update(this.groupStudentsScoreListId, this.dataSource);
  }

  /**
   * Get the role of the logged user
   * @private
   */
  private getLoggedUserRole$(): Observable<any> {
    return this.profileService.getLoggedUserRole$().pipe(
      tap((userRole) => {
        this.userRole = userRole;
      })
    );
  }

  /**
   * Get the columns of the table
   * @param columnsData
   * @param sortable
   * @param dataSourceColumnsList
   * @private
   */
  private getColumns(columnsData: any, sortable: boolean = true, dataSourceColumnsList?: any): TableColumn[] {
    const columnList = columnsData.map((column) => {
      if (column.name === 'totalPoints') column.name = `reports.${column.name}`;
      const newColumn: TableColumn = {
        source: column['id'],
        dataType: DataTypes.number,
        label: this.translateService.instant(column.name),
        sortable: sortable,
        labelSort: column['id'],
        allowFiltering: column['userEmail'] === 'userEmail' && sortable ? true : false,
        style: { width: '120px' },
        showInColumnChooser: true,
        isRanking: column['isRanking'],
        visible: dataSourceColumnsList
          ? dataSourceColumnsList.find((columnData) => columnData['source'] === column['id'])['visible']
          : true,
        valueParser: (value, data) => {
          return data[column.id];
        }
      };
      return newColumn;
    });

    columnList.unshift({
      source: 'userEmail',
      dataType: DataTypes.string,
      label: this.translateService.instant('nouns.email'),
      visible: true,
      sortable: sortable,
      allowFiltering: sortable,
      style: { width: '200px' }
    });

    return columnList;
  }

  /**
   * Load the table
   * @private
   */
  private loadTableDataSource(props: any, dataSource: any, group: any): Observable<any> {
    return this.groupStudentsService.fetch$(props, dataSource, group).pipe(
      tap((response) => {
        if (response && response.columns && !!response.columns.length && response.docs) {
          dataSource.columns = this.getColumns(response.columns, !!response.docs.length, dataSource['columns']);
          dataSource.docs = response.docs;
          dataSource.total = response.total;
          dataSource.limit = response.limit;
          dataSource.page = response.page;
          dataSource.pages = response.pages;
        }
      })
    );
  }

  /**
   * Set the toolbar of table
   * @param hideSearch
   */
  private setToolbarTable(hideSearch: boolean) {
    this.toolbarConfig = {
      ...this.toolbarConfig,
      list: {
        ...this.toolbarConfig.list,
        download: !hideSearch,
        filterRow: !hideSearch,
        hideSearch: hideSearch,
        hideItemsSelector: hideSearch
      }
    };
  }
}
